<script setup lang="ts">
/**
 * WHAT SOLD, WHO SOLD IT, AND WHEN — for the range the sales screen shows.
 *
 * Loads itself, so the sales screen's own figures are never held up by it.
 * Each view downloads as a CSV a Czech Excel opens as columns.
 */
import { computed, ref, watch } from "vue";
import type { Breakdown, ConsoleApi } from "../api";
import { money } from "../format";
import { csvAmount, downloadCsv, toCsv } from "../csv";

const props = defineProps<{
  api: Pick<ConsoleApi, "breakdown">;
  from: string;
  to: string;
  locale: string;
  t: (key: string) => string;
  /** Staff ids to names; an id nobody names shows as itself. */
  staffNames: Readonly<Record<string, string>>;
}>();

const tab = ref<"items" | "staff" | "hours">("items");
const data = ref<Breakdown | null>(null);
const failed = ref(false);

const load = async (): Promise<void> => {
  failed.value = false;
  try {
    data.value = await props.api.breakdown(props.from, props.to);
  } catch {
    failed.value = true;
  }
};
watch(() => [props.from, props.to], load, { immediate: true });

const qty = (milli: number): string => new Intl.NumberFormat(props.locale).format(milli / 1000);
const name = (staffId: string): string => props.staffNames[staffId] ?? staffId;

/** Items under their categories, in the menu's order, with a subtotal each. */
const groups = computed(() => {
  const d = data.value;
  if (!d) return [];
  const order = [...d.categories.map((c) => c.categoryId), null];
  return order
    .map((categoryId) => {
      const items = d.items.filter((i) => i.categoryId === categoryId);
      return {
        categoryId,
        name: d.categories.find((c) => c.categoryId === categoryId)?.name ?? props.t("sales.other"),
        items,
        chargedMinor: items.reduce((sum, i) => sum + i.chargedMinor, 0n),
      };
    })
    .filter((g) => g.items.length > 0);
});

const busiest = computed(() => Math.max(1, ...(data.value?.hours ?? []).map((h) => Number(h.salesMinor))));

const csv = (): void => {
  const d = data.value;
  if (!d) return;
  const range = `${props.from}_${props.to}`;
  if (tab.value === "items") {
    downloadCsv(
      `polozky_${range}.csv`,
      toCsv([
        [props.t("sales.category"), props.t("sales.item"), props.t("sales.quantity"), props.t("sales.discounts"), props.t("sales.revenue")],
        ...groups.value.flatMap((g) => g.items.map((i) => [g.name, i.name, i.quantityMilli / 1000, csvAmount(i.discountMinor + i.compMinor), csvAmount(i.chargedMinor)])),
      ]),
    );
  } else if (tab.value === "staff") {
    downloadCsv(
      `obsluha_${range}.csv`,
      toCsv([
        [props.t("sales.server"), props.t("sales.checkCount"), props.t("sales.revenue"), props.t("sales.average"), props.t("sales.tips"), props.t("sales.discounts"), props.t("sales.voids")],
        ...d.staff.map((r) => [name(r.staffId), r.checkCount, csvAmount(r.salesMinor), csvAmount(r.averageMinor), csvAmount(r.tipMinor), csvAmount(r.discountMinor + r.compMinor), r.voidCount]),
      ]),
    );
  } else {
    downloadCsv(
      `hodiny_${range}.csv`,
      toCsv([[props.t("sales.hour"), props.t("sales.checkCount"), props.t("sales.revenue")], ...d.hours.map((h) => [`${h.hour}:00`, h.checkCount, csvAmount(h.salesMinor)])]),
    );
  }
};
</script>

<template>
  <section class="gf-panel breakdown">
    <header class="breakdown__bar">
      <nav class="breakdown__tabs" role="tablist" :aria-label="t('sales.breakdown')">
        <button
          v-for="name in ['items', 'staff', 'hours'] as const"
          :key="name"
          type="button"
          role="tab"
          class="gf-button breakdown__tab"
          :aria-selected="tab === name"
          :data-tab="name"
          @click="tab = name"
        >
          {{ t(`sales.tab.${name}`) }}
        </button>
      </nav>
      <button type="button" class="gf-button" :disabled="!data" data-testid="csv" @click="csv">CSV</button>
    </header>

    <p v-if="failed" class="gf-note gf-note--bad" role="alert">
      {{ t("common.failed") }} <button type="button" class="gf-button" @click="load">{{ t("common.retry") }}</button>
    </p>
    <p v-else-if="!data" class="gf-muted" role="status">{{ t("common.loading") }}</p>

    <div v-else-if="tab === 'items'" class="gf-scroll">
      <table class="gf-table">
        <thead>
          <tr>
            <th scope="col">{{ t("sales.item") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.quantity") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.discounts") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.revenue") }}</th>
          </tr>
        </thead>
        <tbody v-for="g in groups" :key="g.categoryId ?? 'other'">
          <tr class="breakdown__group">
            <th scope="rowgroup" colspan="3">{{ g.name }}</th>
            <td class="gf-num">{{ money(g.chargedMinor, locale) }}</td>
          </tr>
          <tr v-for="i in g.items" :key="i.itemRef" :data-item="i.itemRef">
            <th scope="row" class="breakdown__item">{{ i.name }}</th>
            <td class="gf-num">{{ qty(i.quantityMilli) }}</td>
            <td class="gf-num gf-muted">{{ i.discountMinor + i.compMinor > 0n ? money(-(i.discountMinor + i.compMinor), locale) : "" }}</td>
            <td class="gf-num">{{ money(i.chargedMinor, locale) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="tab === 'staff'" class="gf-scroll">
      <table class="gf-table">
        <thead>
          <tr>
            <th scope="col">{{ t("sales.server") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.checkCount") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.revenue") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.average") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.tips") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.discounts") }}</th>
            <th scope="col" class="gf-num">{{ t("sales.voids") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in data.staff" :key="r.staffId" :data-staff="r.staffId">
            <th scope="row">{{ name(r.staffId) }}</th>
            <td class="gf-num">{{ r.checkCount }}</td>
            <td class="gf-num">{{ money(r.salesMinor, locale) }}</td>
            <td class="gf-num">{{ money(r.averageMinor, locale) }}</td>
            <td class="gf-num">{{ money(r.tipMinor, locale) }}</td>
            <td class="gf-num">{{ money(r.discountMinor + r.compMinor, locale) }}</td>
            <td class="gf-num">{{ r.voidCount }} <span class="gf-muted">· {{ money(r.voidGrossMinor, locale) }}</span></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="breakdown__hours">
      <div v-for="h in data.hours.filter((row) => row.checkCount > 0 || row.salesMinor !== 0n)" :key="h.hour" class="breakdown__hour" :data-hour="h.hour">
        <span class="breakdown__hourLabel gf-num">{{ h.hour }}:00</span>
        <span class="breakdown__meter" :style="{ width: `${(Number(h.salesMinor) / busiest) * 100}%` }" />
        <span class="gf-num">{{ money(h.salesMinor, locale) }}</span>
        <span class="gf-muted gf-num">{{ h.checkCount }}×</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.breakdown__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* MyMonday's segmented control: a sunken track, the chosen tab lifted out. */
.breakdown__tabs {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--gf-color-surface-sunken);
  border-radius: var(--gf-radius-md);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border-subtle);
}

.breakdown__tab {
  min-height: 30px;
  color: var(--gf-color-text-muted);
  background: transparent;
  border-color: transparent;
  border-radius: var(--gf-radius-sm);
  box-shadow: none;
}

.breakdown__tab[aria-selected="true"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  border-color: var(--gf-color-border-subtle);
  box-shadow: var(--gf-shadow-sm);
  font-weight: var(--gf-weight-semibold);
}

.breakdown__group th,
.breakdown__group td {
  padding-top: var(--gf-space-4);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-semibold);
  color: var(--gf-color-text-muted);
}

/* Same left edge as the category above it: 3 px short of it read as a typo. */
.breakdown__item {
  font-weight: var(--gf-weight-normal);
}

.breakdown__hours {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
}

.breakdown__hour {
  display: grid;
  grid-template-columns: 3.5rem 1fr 7rem 3rem;
  align-items: center;
  gap: var(--gf-space-2);
}

.breakdown__hourLabel {
  color: var(--gf-color-text-muted);
}

/*
  Ink, not the accent: the red is kept for the one action a screen exists for
  (see `tokens.ts`), and a column of red bars would spend it on decoration.
*/
.breakdown__meter {
  display: block;
  height: 0.625rem;
  min-width: 2px;
  border-radius: var(--gf-radius-full);
  background: var(--gf-color-border-strong);
}
</style>
